import { useAtomValue } from 'jotai'
import React from 'react'
import * as R from 'remeda'

import {
  calcNineSegmentsRatioAndIndicators,
  findByPath,
  mapNineSegment,
  type NineSegmentCounts,
  type NineSegmentIndicator,
  pathOr,
  toNineNumber,
} from '~/utils'

import { countColumnKeysAtom, tabulationResultAtom } from '../atoms'
import { type CountColumnKey, countColumnKeyToId } from '../utils'

import { useTabulatedBrands } from './useTabulatedBrands'

type NineSegmentsBrandData = {
  countColumnKey: CountColumnKey
  brandId: number
  brandName: string
  brandNumber: number
  counts: NineSegmentCounts
  ratios: NineSegmentCounts & Record<NineSegmentIndicator, number>
}

export const useNineSegmentsBrandsData = (): NineSegmentsBrandData[] => {
  const countColumnKeys = useAtomValue(countColumnKeysAtom)
  const brands = useTabulatedBrands()
  const tabulationResult = useAtomValue(tabulationResultAtom)

  return React.useMemo(
    () =>
      countColumnKeys.flatMap((countColumnKey) =>
        brands.map((brand) => {
          const result = findByPath(
            tabulationResult ?? [],
            'byCustomerAttributeId',
            countColumnKeyToId(countColumnKey),
          )?.result
          const counts = mapNineSegment((nineSegment) =>
            R.pipe(
              result,
              pathOr('brands', []),
              findByPath('brandNumber', brand.number),
              pathOr('result', []),
              findByPath('segmentNumber', toNineNumber(nineSegment)),
              pathOr('count', NaN),
            ),
          )
          const ratios = calcNineSegmentsRatioAndIndicators(
            counts,
            result?.totalCount ?? NaN,
          )

          return {
            countColumnKey,
            brandId: brand.id,
            brandName: brand.name,
            brandNumber: brand.number,
            counts,
            ratios,
          }
        }),
      ),
    [brands, countColumnKeys, tabulationResult],
  )
}
